<script setup lang="ts">
import { sendMail } from '#imports'
import { ref } from 'vue'
import AppButton from '~/components/AppButton.vue'
import type { CmsCustomComponentType } from '~/layers/cms/app/types/cmsTypes'

const sending = ref<boolean>(false)
const error = ref<string | null>(null)
const success = ref<boolean>(false)

const send = async (e: SubmitEvent) => {
  e.preventDefault()

  if (sending.value) return

  sending.value = true
  success.value = false
  error.value = null

  const form = e.target as HTMLFormElement
  const data = new FormData(form)
  const payload = Object.fromEntries(data.entries())
  const response = await sendMail(payload)

  if (response.status_ok) {
    success.value = true
  } else {
    error.value = response.message ?? ''
  }

  sending.value = false
}

const props = defineProps<{
  cmsDataPath: (string | number)[]
}>()
const nazwa_komponentu = 'Contact/ContactForm'
const component_default_values: CmsCustomComponentType = {
  id: { value: undefined, default_value: 'Lorem Ipsum', settingType: 'text', setting_label: 'zmien id' }
}
</script>

<template>
  <CmsCustomComponent
    v-slot="{ component_values }"
    :cms-data-path="[...props.cmsDataPath]"
    :component-name="nazwa_komponentu"
    :defaults="component_default_values"
    :open-options="true"
  >
    <div
      :id="component_values?.id?.value"
      class="py-7 xl:py-20 xl:px-2xl"
    >
      <div class="bg-background-1 rounded-4xl xl:rounded-[70px] pt-10 pb-10 xl:pb-24 px-xl xl:px-18 flex flex-col gap-20">
        <div class="flex flex-col gap-7.5">
          <span class="text-H2 text-Tekst">
            <CmsText :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_1']" />
          </span>
          <span class="text-H4 text-Tekst">
            <CmsText :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_2']" />
          </span>
        </div>
        <form
          v-if="!success"
          class="xl:px-24 grid xl:grid-cols-2 xl:grid-rows-1 gap-6 xl:gap-11"
          @submit="send"
        >
          <div class="flex flex-col  gap-6 xl:gap-10 w-full">
            <label
              for="name"
              class="flex flex-col gap-5 w-full"
            >
              <span class="text-Tekst text-Paragraph font-bold! px-7.5">
                Imię i nazwisko *
              </span>
              <input
                id="name"
                type="text"
                name="name"
                placeholder="Wpisz swoje imię i nazwisko"
                class="py-5 px-7.5 text-Tekst text-Paragraph border border-Tekst rounded-3xl"
                required
              >
            </label>
            <div class="grid xl:grid-cols-2 grid-rows-1  gap-6 xl:gap-10">
              <label
                for="phone"
                class="flex flex-col gap-5 w-full"
              >
                <span class="text-Tekst text-Paragraph font-bold! px-7.5">
                  Telefon
                </span>
                <input
                  id="phone"
                  type="phone"
                  name="phone"
                  placeholder="Wpisz swój numer telefonu"
                  class="py-5 px-7.5 text-Tekst text-Paragraph border border-Tekst rounded-3xl"
                >
              </label>
              <label
                for="name"
                class="flex flex-col gap-5 w-full"
              >
                <span class="text-Tekst text-Paragraph font-bold! px-7.5">
                  E-mail *
                </span>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Wpisz swój adres e-mail"
                  class="py-5 px-7.5 text-Tekst text-Paragraph border border-Tekst rounded-3xl"
                  required
                >
              </label>
            </div>
            <div class="flex flex-col gap-5 w-full">
              <span class="text-Tekst text-Paragraph font-bold! px-7.5">
                Wybierz co Cię interesuje
              </span>
              <div class="flex flex-col xl:flex-row justify-between items-start gap-8">
                <label
                  for="mieszkanie_na_wlasnosc"
                  class="flex flex-row items-start gap-2 cursor-pointer"
                >
                  <input
                    id="mieszkanie_na_wlasnosc"
                    type="checkbox"
                    name="mieszkanie_na_wlasnosc"
                    class="peer sr-only"
                  >
                  <span
                    class="flex size-5 shrink-0 items-center justify-center rounded-full border border-[#4c5751]
                        after:size-3 after:rounded-full after:bg-black after:scale-0 after:transition-transform
                        peer-checked:after:scale-100"
                  />
                  <span class="text-Paragraph text-Tekst">Mieszkanie na własność</span>
                </label>
                <label
                  for="wynajem"
                  class="flex flex-row items-start gap-2 cursor-pointer"
                >
                  <input
                    id="wynajem"
                    type="checkbox"
                    name="wynajem"
                    class="peer sr-only"
                  >
                  <span
                    class="flex size-5 shrink-0 items-center justify-center rounded-full border border-[#4c5751]
                        after:size-3 after:rounded-full after:bg-black after:scale-0 after:transition-transform
                        peer-checked:after:scale-100"
                  />
                  <span class="text-Paragraph text-Tekst">Wynajem</span>
                </label>
                <label
                  for="prywatny_dom_opieki"
                  class="flex flex-row items-start gap-2 cursor-pointer"
                >
                  <input
                    id="prywatny_dom_opieki"
                    type="checkbox"
                    name="prywatny_dom_opieki"
                    class="peer sr-only"
                  >
                  <span
                    class="flex size-5 shrink-0 items-center justify-center rounded-full border border-[#4c5751]
                        after:size-3 after:rounded-full after:bg-black after:scale-0 after:transition-transform
                        peer-checked:after:scale-100"
                  />
                  <span class="text-Paragraph text-Tekst">Prywatny dom opieki</span>
                </label>
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-10 w-full">
            <label
              for="message"
              class="flex flex-col gap-5 w-full"
            >
              <span class="text-Tekst text-Paragraph font-bold! px-7.5">
                Wiadomość
              </span>
              <textarea
                id="message"
                name="message"
                placeholder="Napisz kilka słów o swojej sytuacji. Każda informacja pomoże nam lepiej odpowiedzieć na Twoje pytania."
                class="py-5 px-7.5 text-Tekst text-Paragraph border border-Tekst rounded-3xl min-h-35"
              />
            </label>
            <div class="flex flex-col gap-4">
              <label
                for="zgoda_1"
                class="flex flex-row gap-2 items-start cursor-pointer"
              >
                <input
                  id="zgoda_1"
                  type="checkbox"
                  name="zgoda"
                  class="peer sr-only"
                >
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border border-[#4c5751]
                      after:size-3 after:rounded-full after:bg-black after:scale-0 after:transition-transform
                      peer-checked:after:scale-100"
                />
                <span class="text-Paragraph text-Tekst">
                  Wyrażam zgodę na otrzymywanie informacji handlowych drogą elektroniczną.
                </span>
              </label>
              <label
                for="zgoda_2"
                class="flex flex-row gap-2 items-start cursor-pointer"
              >
                <input
                  id="zgoda_2"
                  type="checkbox"
                  name="zgoda"
                  class="peer sr-only"
                  required
                >
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border border-[#4c5751]
                      after:size-3 after:rounded-full after:bg-black after:scale-0 after:transition-transform
                      peer-checked:after:scale-100"
                />
                <span class="text-Paragraph text-Tekst">
                  * Wyrażam zgodę na przetwarzanie moich danych osobowych w celu udzielenia odpowiedzi na przesłane zapytanie zgodnie z <NuxtLink
                    to="/polityka-prywatnosci/"
                    class="underline"
                  >Polityką Prywatności</NuxtLink>
                </span>
              </label>
              <label
                for="zgoda_3"
                class="flex flex-row gap-2 items-start cursor-pointer"
              >
                <input
                  id="zgoda_3"
                  type="checkbox"
                  name="zgoda"
                  class="peer sr-only"
                  required
                >
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border border-[#4c5751]
                      after:size-3 after:rounded-full after:bg-black after:scale-0 after:transition-transform
                      peer-checked:after:scale-100"
                />
                <span class="text-Paragraph text-Tekst">
                  * Zapoznałem/-am się z <NuxtLink
                    to="/polityka-prywatnosci/"
                    class="underline"
                  >Polityką Prywatności</NuxtLink> i akceptuję jej postanowienia.
                </span>
              </label>
            </div>
            <div class="flex justify-end">
              <AppButton
                :class="sending ? 'pointer-events-none opacity-50' : ''"
                button-theme="Primary"
                submit
                arrow
              >
                <span class="link-text">Wyślij wiadomość</span>
              </AppButton>
            </div>
          </div>
        </form>
      </div>
      <Teleport to="body">
        <div
          class="fixed left-1/2 top-1/2 -translate-1/2 bg-background-1 rounded-2xl shadow-2xl p-5 flex flex-col gap-2.5 duration-200 transition-all max-h-[80svh] overflow-y-auto"
          :class="success ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'"
        >
          <div
            class="rounded-full border border-Tekst self-end w-8 h-8 flex items-center justify-center cursor-pointer"
            @click="success=false"
          >
            <i class="pi pi-times text-xl! text-Tekst" />
          </div>
          <div class="px-xl flex flex-col gap-2.5">
            <span class="text-H2 text-Tekst">Twoje zapytanie zostało do nas wysłane.</span>
            <div class="h-px bg-gray-400 w-full" />
            <span class="text-H4 text-Tekst">Dziękujemy za zainteresowanie naszą ofertą. Skontaktujemy się z Tobą jak najszybciej.</span>
            <AppButton
              button-theme="Secondary"
              class="self-center w-fit"
              @click="success=false"
            >
              <span>Zamknij</span>
            </AppButton>
          </div>
        </div>
        <div
          class="fixed left-1/2 top-1/2 -translate-1/2 bg-background-1 rounded-2xl shadow-2xl p-5 flex flex-col gap-2.5 duration-200 transition-all max-h-[80svh] overflow-y-auto"
          :class="error ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'"
        >
          <div
            class="rounded-full border border-Tekst self-end w-8 h-8 flex items-center justify-center cursor-pointer"
            @click="error=null"
          >
            <i class="pi pi-times text-xl! text-Tekst" />
          </div>
          <div class="px-xl flex flex-col gap-2.5">
            <span class="text-H2 text-Primary">Coś poszło nie tak!</span>
            <div class="h-px bg-gray-400 w-full" />
            <span class="text-H4 text-Tekst">
              Przepraszamy, ale nie udało się wysłać tej wiadomości.
              <br>
              Przejdź do formularza i spróbuj wysłać zapytanie ponownie.
              <br>
              {{ error }}
            </span>
            <AppButton
              button-theme="Primary"
              class="self-center w-fit"
              @click="error=null"
            >
              <span>Wróć do formularza</span>
            </AppButton>
          </div>
        </div>
      </Teleport>
    </div>
  </CmsCustomComponent>
</template>
